import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardFooter, Container, Row, Col, CardHeader, UncontrolledAlert } from 'reactstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiService } from 'services';
import { getQueryString } from 'services/ApiService';
import BSOTable from './BSOTable';
import Pagination from 'components/Pagination';
import Filters, { Field } from 'components/Filters';
import Header from 'components/Headers/Header';
import { useUser } from 'context/UserContext';
import { BSO, Response } from './types';
import config from 'config';

interface Options {
  [key: string]: any
}

export default function BSOList() {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<BSO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentOptions, setCurrentOptions] = useState<Options | undefined>();
  const [success, setSuccess] = useState<boolean | undefined>(false);
  const [error, setError] = useState<string | undefined>();
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const replace = useNavigate();

  const location = useLocation();
  const user = useUser();

  const statusOptions: Options = {
    pending: 'Pendiente',
    presented: 'Presentado',
    observed: 'Observado',
    approved: 'Aprobado',
  };

  const delegationOptions: Options = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10',
  };

  const filterFields: Field[] = [
    {
      name: 'Estado',
      field: 'status',
      type: 'select',
      selectOptions: statusOptions,
      defaultKey: 'pending',
      colSize: 3,
    },
    {
      name: 'Delegación',
      field: 'delegation',
      type: 'select',
      selectOptions: delegationOptions,
      defaultKey: 'any',
      colSize: 3,
    },
    { name: 'Remito', field: 'remito_id', type: 'number', colSize: 3 },
    { name: 'N° Factura', field: 'nroFactura', type: 'text', colSize: 3 },
  ];

  useEffect(() => {
    fetchData({ page: 1, status: 'pending' });
  }, []);

  const fetchData = (async (options: Options) => {
    setIsLoading(true);
    const currentOpt = { ...currentOptions, ...options };
    const res = await ApiService.getAll('submission-orders', { ...currentOpt }) as Response<BSO>;
    const { rows, count } = res;
    setCurrentOptions(currentOpt);
    setData(rows);
    setTotal(count);
    setIsLoading(false);
  });

  const filter = async ({ key, value }: { key: string, value: any }) => {
    await fetchData({ [key]: value });
  };

  const changePage = async (page: number) => {
    await fetchData({ page });
    setCurrentPage(page);
  };

  useEffect(() => {
    if (location.state) {
      const { success } = location.state;
      if (success !== undefined) {
        replace(location.pathname, { state: {} });
        setSuccess(success);
      } else {
        setSuccess(undefined);
      }
    }
  }, [replace, location.state, location.pathname]);

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return;
      }

      setIsLoadingExcel(true);
      setError(undefined);

      const queryString = getQueryString({ ...currentOptions });

      const res = await fetch(
        `${config.baseUrl}/submission-orders/all${queryString}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        },
      );

      if (!res.ok) {
        let message = 'No se pudo generar el reporte Excel.';
        try {
          const body = await res.text();
          if (body) {
            const parsed = JSON.parse(body);
            if (typeof parsed === 'string' && parsed) {
              message = parsed;
            } else if (parsed?.message) {
              message = parsed.message;
            }
          }
        } catch {
          // ignoramos errores al parsear la respuesta
        }
        setError(message);
        return;
      }

      const blob = await res.blob();

      const file = window.URL.createObjectURL(blob);

      window.location.assign(file);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingExcel(false);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Filters fields={filterFields} onFilter={ filter } open={true} />
            <Card className='shadow'>
              <CardHeader className='border-0'>
                {
                  success &&
                  <UncontrolledAlert color="success">
                    <strong>Éxito!</strong> La operación se realizó correctamente.
                  </UncontrolledAlert>
                }
                {
                  error &&
                  <UncontrolledAlert color="danger">
                    <strong>Error!</strong> { error }
                  </UncontrolledAlert>
                }
                <Row className='align-items-center'>
                  <Col md='6'>
                    <h3>Lista de Pedidos de Presentación de Facturas</h3>
                  </Col>
                  {
                    user.is([1, 4])
                      ? <>
                          <Col md='6' className='text-right'>
                            <Button
                              color="light"
                              onClick={ downloadExcel }
                              disabled={ isLoadingExcel }
                              className='btn btn-md'
                            >
                              {
                                isLoadingExcel
                                  ? <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                  : <>Descargar Excel</>
                              }
                            </Button>
                            <Link to='/admin/pedidos-presentacion/nuevo' style={{ marginRight: '1rem' }}>
                              <Button color='primary' className='btn btn-md'>
                                Generar pedidos
                              </Button>
                            </Link>
                            <Link to='/admin/pedidos-presentacion/cierre' style={{ marginRight: '1rem' }}>
                              <Button color='primary' className='btn btn-md'>
                                Cerrar pedido
                              </Button>
                            </Link>
                          </Col>
                        </>
                      : null
                  }
                </Row>
              </CardHeader>
              <CardBody className='p-0'>
                <BSOTable
                  items={data}
                  isLoading={isLoading}
                />
              </CardBody>
              <CardFooter className='py-4'>
                {/* Pagination */}
                <Pagination
                  page={currentPage}
                  count={total}
                  onChangePage={ changePage }
                  limit={ 10 }
                />
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
